import { apiClient } from '@/core/api/client'

const BASE = '/api/admin/lea-v3'

export interface DashboardData {
  timeouts: {
    total: number
    expired: number
    pending: number
    categories: number
  }
  ladder_breakdown: Array<{ category: string; ladder_slot: string | null; n: number }>
  route_distribution: Array<{ trigger: string; unit: string; n: number; min_delay: number; max_delay: number }>
  affaires: { total: number; active: number }
}

export interface RouteRow {
  id: string
  applies_to_status: string[]
  applies_to_category: string[]
  applies_to_sender_role: string[]
  applies_to_message_type: string[]
  applies_to_trigger: string[]
  priority: number
  process_delay_hours: number
  process_delay_unit: 'hours' | 'calendar_days' | 'working_days'
  auto_execute: boolean
  must_use_template: string | null
  suggested_templates: string[]
  timeout_reason_pattern: string | null
  knowledge_files: string[]
}

export interface PlanStep {
  type: 'email' | 'tracker_com' | 'sms' | string
  content: Record<string, unknown>
  addresses_thread_ids?: number[]
}

export interface SamplePlan {
  id: string
  affaire: { affaire_id: number; status: string; last_status_change_at: string }
  trigger: { type: string; category: string; ladder_slot: string | null; fired_at: string }
  matched_route: {
    id: string
    applies_to_status: string[]
    applies_to_category: string[]
    process_delay: string
    auto_execute: boolean
    must_use_template: string | null
    knowledge_files_count: number
    priority: number
  }
  prompt_meta: {
    total_chars: number
    knowledge_layers: string[]
    dossier_thread_count: number
  }
  claude_output: {
    analysis: string
    recommendation: string
    steps: PlanStep[]
    estimated_token_usage?: { input: number; output: number }
  }
}

export interface TimeoutRow {
  affaire_id: number
  status: string
  category: string
  ladder_slot: string | null
  expires_at: string
  set_at: string
  set_by: string
  reason: string | null
  is_expired: boolean
}

export const leaV3API = {
  getDashboard: () =>
    apiClient.get<{ success: boolean; data: DashboardData }>(`${BASE}/dashboard`),

  getRoutes: (filters?: { trigger?: string; category?: string }) => {
    const params = new URLSearchParams()
    if (filters?.trigger) params.set('trigger', filters.trigger)
    if (filters?.category) params.set('category', filters.category)
    const q = params.toString()
    return apiClient.get<{ success: boolean; data: RouteRow[] }>(
      `${BASE}/routes${q ? `?${q}` : ''}`
    )
  },

  updateRoute: (id: string, payload: { process_delay_hours: number; process_delay_unit: string }) =>
    apiClient.patch<{ success: boolean; data: RouteRow }>(`${BASE}/routes/${id}`, payload),

  getTimeouts: (filters?: { category?: string; ladder_slot?: string; expired?: boolean; limit?: number }) => {
    const params = new URLSearchParams()
    if (filters?.category) params.set('category', filters.category)
    if (filters?.ladder_slot) params.set('ladder_slot', filters.ladder_slot)
    if (filters?.expired !== undefined) params.set('expired', String(filters.expired))
    if (filters?.limit) params.set('limit', String(filters.limit))
    const q = params.toString()
    return apiClient.get<{ success: boolean; data: TimeoutRow[]; count: number }>(
      `${BASE}/timeouts${q ? `?${q}` : ''}`
    )
  },

  getActivity: () =>
    apiClient.get<{ success: boolean; data: unknown[]; note?: string }>(`${BASE}/activity`),

  getSamplePlans: () =>
    apiClient.get<{ success: boolean; generated_at: string | null; plans: SamplePlan[]; note?: string }>(
      `${BASE}/sample-plans`
    ),

  getSamplePlanPrompt: (id: string) =>
    apiClient.get<string>(`${BASE}/sample-plans/${id}/prompt`, { responseType: 'text' as any, transformResponse: (v: any) => v }),

  getAffaireHistory: (affaireId: number) =>
    apiClient.get<{ success: boolean; data: AffaireHistory }>(
      `${BASE}/affaire/${affaireId}/history`
    ),

  getPlans: (filters?: { affaire_id?: number; status?: string; limit?: number }) => {
    const params = new URLSearchParams()
    if (filters?.affaire_id) params.set('affaire_id', String(filters.affaire_id))
    if (filters?.status) params.set('status', filters.status)
    if (filters?.limit) params.set('limit', String(filters.limit))
    const q = params.toString()
    return apiClient.get<{ success: boolean; data: PlanListRow[]; count: number }>(
      `${BASE}/plans${q ? `?${q}` : ''}`
    )
  },

  getPlanDetail: (id: string) =>
    apiClient.get<{ success: boolean; data: PlanDetail }>(`${BASE}/plans/${id}`)
}

export interface PlanListRow {
  id: string
  affaire_id: number
  status: string
  category: string | null
  triggered_by: string | null
  message_type: string | null
  analysis_preview: string | null
  step_count: number
  is_mock: boolean | null
  model: string | null
  ladder_slot: string | null
  input_tokens: number | null
  output_tokens: number | null
  auto_execute: boolean
  proposed_at: string
  validated_at: string | null
  executed_at: string | null
  cancelled_at: string | null
  matched_route_ids: string[]
}

export interface PlanDetail extends PlanListRow {
  analysis: string
  steps: Array<{ type: string; content: Record<string, unknown>; addresses_thread_ids?: number[] }>
  context_snapshot: Record<string, unknown>
  validated_by: string | null
  validation_comment: string | null
  cancellation_reason: string | null
}

export interface RouteApplication {
  id: string
  route_id: string | null
  ladder_slot: string | null
  category: string | null
  trigger_type: 'timeout' | 'new_input'
  fired_at: string
  outcome: 'plan_created' | 'no_action_already_handled' | 'no_route' | 'pending_validation' | 'auto_executed' | 'failed'
  plan_id: string | null
  reason: string | null
  steps_summary: Record<string, number> | null
}

export interface ThreadEvent {
  id: string
  sub_type: string
  sender_role: string
  author_name: string | null
  author_email: string | null
  email_subject: string | null
  email_has_attachments: boolean | null
  content_preview: string | null
  created_at: string
  category: string | null
  urgency: string | null
  is_read: boolean | null
}

export interface AffaireHistory {
  affaire: {
    affaire_id: number
    status: string
    is_active: boolean
    last_status_change_at: string
    last_msg_type: string | null
    last_sender: string | null
    last_activity_at: string | null
  }
  current_timeout: {
    expires_at: string
    category: string
    ladder_slot: string | null
    set_at: string
    set_by: string
    reason: string
    is_expired: boolean
  } | null
  route_applications: RouteApplication[]
  thread_events: ThreadEvent[]
  ladder_history: Record<string, Array<{ ladder_slot: string; fired_at: string; outcome: string; route_id: string }>>
}
